class AudioPlayer extends HTMLElement {
  // --- Component Properties ---
  private audioElement: HTMLAudioElement | null = null;
  private playIcon: HTMLElement | null = null;
  private pauseIcon: HTMLElement | null = null;
  private indicator: HTMLElement | null = null;
  private track: HTMLElement | null = null;
  private volumeIcon: HTMLElement | null = null;
  private dotIcon: HTMLElement | null = null;
  private timeDisplay: HTMLElement | null = null;
  private menu: HTMLElement | null = null; // For the new dropdown menu

  // --- Cached Volume Icons for Performance ---
  private volumeMuteIcon: HTMLElement | null = null;
  private volumeLowIcon: HTMLElement | null = null;
  private volumeMediumIcon: HTMLElement | null = null;
  private volumeHighIcon: HTMLElement | null = null;

  // --- Component State ---
  private isPlaying: boolean = false;
  private isDragging: boolean = false;
  private isMenuVisible: boolean = false; // To track menu state

  // --- Bound Event Handlers ---
  private onDraggingHandler: (e: MouseEvent) => void;
  private stopDraggingHandler: () => void;
  private onOutsideClickHandler: () => void; // For closing the menu

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });

    // Bind 'this' context for document-level event listeners
    this.onDraggingHandler = this.onDragging.bind(this);
    this.stopDraggingHandler = this.stopDragging.bind(this);
    this.onOutsideClickHandler = this.handleOutsideClick.bind(this);
  }

  /**
   * Called when the element is added to the DOM.
   */
  connectedCallback() {
    this.audioElement = this.querySelector('audio');
    if (!this.audioElement) {
      console.error("AudioPlayer component requires an <audio> element as a child.");
      return;
    }

    this.render();
    this.initAudioElement();
    this.queryDOMElements();
    this.initializeState();
    this.setupEventListeners();
  }

  /**
   * Called when the element is removed from the DOM.
   */
  disconnectedCallback() {
    // Clean up global event listeners to prevent memory leaks
    document.removeEventListener('mousemove', this.onDraggingHandler);
    document.removeEventListener('mouseup', this.stopDraggingHandler);
    document.removeEventListener('click', this.onOutsideClickHandler); // Cleanup for menu listener
  }

  // --- Private Initialization Methods ---

  private render() {
    if (!this.shadowRoot) return;
    this.shadowRoot.innerHTML = `
        <style>
            :host {
                display: inline-block;
                width: 382px; /* Match SVG width */
            }

            .audio-player-component {
                position: relative; /* Needed for dropdown positioning */
            }

            .icon, .track {
                cursor: pointer;
            }

            .pause-icon {
                display: none;
            }

            .time-display {
                dominant-baseline: middle;
                text-anchor: start;
                font-size: 14px;
                user-select: none;
            }

            /* --- Dropdown Menu Styles --- */
            .menu-dropdown {
                position: absolute;
                right: 0px;
                top: 56px; /* Position below the player */
                background-color: white;
                border: 1px solid #D1D1D1;
                border-radius: 8px;
                box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                padding: 0px 0;
                z-index: 10;
                min-width: 200px;
            }

            .menu-dropdown a {
                display: flex;
                padding: 8px 15px;
                color: black;
                text-decoration: none;
                font-size: 14px;
                justify-content: space-between;
                justify-items: center;
                align-items: center;
            }

            .menu-dropdown a:hover {
                background-color: #f0f0f0;
            }
            .dot-icon {
                display: block;
                padding: 4px;
            }
        </style>
        <div class="audio-player-component">
            <svg height="52" viewBox="0 0 382 52" fill="none" xmlns="http://www.w3.org/2000/svg" style="width: 100%;">
                <rect x="0" y="0" width="381" height="51" rx="25.5" stroke="#D1D1D1"/>

                <g class="play-icon icon">
                    <path d="M21.333 19.5833V32.4167L31.4163 26L21.333 19.5833Z" fill="black"/>
                </g>

                <g class="pause-icon icon">
                    <rect x="20" y="19.5833" width="3" height="12.8334" fill="black"/>
                    <rect x="25" y="19.5833" width="3" height="12.8334" fill="black"/>
                </g>

                <text x="45" y="26" class="time-display" fill="black">0:00 / 0:00</text>

                <rect x="127" y="24" width="173" height="4" fill="#D9D9D9" class="track" rx="2"/>
                <rect x="127" y="24" width="0" height="4" rx="2" fill="#595959" class="indicator"/>

                <g class="volume-icon icon">
                    <g class="volume-state volume-mute" style="display: none;">
                        <g transform="translate(315, 19) scale(0.9)">
                            <path d="M0,4.58v5.5h3.67l4.58,4.58V0L3.67,4.58H0Z" fill="black"/>
                            <polygon points="20.14 3.35 19.11 2.32 15.13 6.3 11.14 2.32 10.11 3.35 14.1 7.33 10.11 11.32 11.14 12.35 15.13 8.36 19.11 12.35 20.14 11.32 16.16 7.33 20.14 3.35" fill="black"/>
                        </g>
                    </g>
                    <g class="volume-state volume-low" style="display: none;">
                        <g transform="translate(315, 19) scale(0.9)">
                            <path d="M0,4.58v5.5h3.67l4.58,4.58V0L3.67,4.58H0Z" fill="black"/>
                            <path d="M9.29,3.07c1.67.7,2.84,2.35,2.84,4.27s-1.17,3.56-2.83,4.26V3.07Z" fill="black"/>
                        </g>
                    </g>
                    <g class="volume-state volume-medium" style="display: none;">
                        <g transform="translate(315, 18.5) scale(0.9)">
                            <path d="M0,4.97v5.5h3.67l4.58,4.58V.39L3.67,4.97H0Z" fill="black"/>
                            <path d="M10.97,15.32l-.62-1.36c2.43-1.11,4.01-3.56,4.01-6.24s-1.66-5.27-4.23-6.33l.57-1.39c3.13,1.3,5.16,4.33,5.16,7.72s-1.92,6.25-4.88,7.6Z" fill="black"/>
                            <path d="M9.29,3.45c1.67.7,2.84,2.35,2.84,4.27s-1.17,3.56-2.83,4.26V3.45Z" fill="black"/>
                        </g>
                    </g>
                    <g class="volume-state volume-high" style="display: block;">
                        <g transform="translate(315, 18) scale(0.9)">
                            <path d="M0,7.35v5.5h3.67l4.58,4.58V2.77L3.67,7.35H0Z" fill="black"/>
                            <path d="M12.33,20.13l-.65-1.35c3.31-1.6,5.45-5,5.45-8.68S14.93,2.93,11.54,1.36l.63-1.36c3.92,1.82,6.46,5.78,6.46,10.1s-2.47,8.18-6.3,10.03Z" fill="black"/>
                            <path d="M10.97,17.71l-.62-1.36c2.43-1.11,4.01-3.56,4.01-6.24s-1.66-5.27-4.23-6.33l.57-1.39c3.13,1.3,5.16,4.33,5.16,7.72s-1.92,6.25-4.88,7.6Z" fill="black"/>
                            <path d="M9.29,5.84c1.67.7,2.84,2.35,2.84,4.27s-1.17,3.56-2.83,4.26V5.84Z" fill="black"/>
                        </g>
                    </g>
                </g>

                <g class="dot-icon icon">
                    <path d="M357 22.3333C358.009 22.3333 358.834 21.5083 358.834 20.5C358.834 19.4917 358.009 18.6667 357 18.6667C355.992 18.6667 355.167 19.4917 355.167 20.5C355.167 21.5083 355.992 22.3333 357 22.3333ZM357 24.1667C355.992 24.1667 355.167 24.9917 355.167 26C355.167 27.0083 355.992 27.8333 357 27.8333C358.009 27.8333 358.834 27.0083 358.834 26C358.834 24.9917 358.009 24.1667 357 24.1667ZM357 29.6667C355.992 29.6667 355.167 30.4917 355.167 31.5C355.167 32.5083 355.992 33.3333 357 33.3333C358.009 33.3333 358.834 32.5083 358.834 31.5C358.834 30.4917 358.009 29.6667 357 29.6667Z" fill="black"/>
                </g>
            </svg>
            
            <!-- --- Download Dropdown Menu --- -->
            <div class="menu-dropdown" style="display: none;">
                <a href="#" class="download-link" download>Download
<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="26" height="28" viewBox="0 0 26 28" style="transform: scale(0.7)">
<path d="M20 21c0-0.547-0.453-1-1-1s-1 0.453-1 1 0.453 1 1 1 1-0.453 1-1zM24 21c0-0.547-0.453-1-1-1s-1 0.453-1 1 0.453 1 1 1 1-0.453 1-1zM26 17.5v5c0 0.828-0.672 1.5-1.5 1.5h-23c-0.828 0-1.5-0.672-1.5-1.5v-5c0-0.828 0.672-1.5 1.5-1.5h7.266l2.109 2.125c0.578 0.562 1.328 0.875 2.125 0.875s1.547-0.313 2.125-0.875l2.125-2.125h7.25c0.828 0 1.5 0.672 1.5 1.5zM20.922 8.609c0.156 0.375 0.078 0.812-0.219 1.094l-7 7c-0.187 0.203-0.453 0.297-0.703 0.297s-0.516-0.094-0.703-0.297l-7-7c-0.297-0.281-0.375-0.719-0.219-1.094 0.156-0.359 0.516-0.609 0.922-0.609h4v-7c0-0.547 0.453-1 1-1h4c0.547 0 1 0.453 1 1v7h4c0.406 0 0.766 0.25 0.922 0.609z"></path>
</svg>
</a>
            </div>
        </div>
    `;
  }

  private initAudioElement() {
    if (!this.audioElement) return;
    this.audioElement.style.display = 'none'; // Hide original player
    this.audioElement.preload = 'metadata';
  }

  private queryDOMElements() {
    if (!this.shadowRoot) return;
    this.playIcon = this.shadowRoot.querySelector('.play-icon');
    this.pauseIcon = this.shadowRoot.querySelector('.pause-icon');
    this.indicator = this.shadowRoot.querySelector('.indicator');
    this.track = this.shadowRoot.querySelector('.track');
    this.volumeIcon = this.shadowRoot.querySelector('.volume-icon');
    this.dotIcon = this.shadowRoot.querySelector('.dot-icon');
    this.timeDisplay = this.shadowRoot.querySelector('.time-display');
    this.menu = this.shadowRoot.querySelector('.menu-dropdown'); // Find the menu

    // Cache volume state icons for better performance
    if (this.volumeIcon) {
      this.volumeMuteIcon = this.volumeIcon.querySelector('.volume-mute');
      this.volumeLowIcon = this.volumeIcon.querySelector('.volume-low');
      this.volumeMediumIcon = this.volumeIcon.querySelector('.volume-medium');
      this.volumeHighIcon = this.volumeIcon.querySelector('.volume-high');
    }
  }

  private initializeState() {
    this.updateVolumeIcon();
    if (this.audioElement && !isNaN(this.audioElement.duration)) {
      this.updateTimeDisplay();
    }
  }

  private setupEventListeners() {
    if (!this.audioElement) return;

    this.playIcon?.addEventListener('click', () => this.togglePlayPause());
    this.pauseIcon?.addEventListener('click', () => this.togglePlayPause());

    this.audioElement.addEventListener('loadedmetadata', () => this.updateTimeDisplay());
    this.audioElement.addEventListener('timeupdate', () => this.updateProgress());
    this.audioElement.addEventListener('ended', () => this.onAudioEnded());
    this.audioElement.addEventListener('play', () => this.onPlay());
    this.audioElement.addEventListener('pause', () => this.onPause());
    this.audioElement.addEventListener('volumechange', () => this.updateVolumeIcon());

    this.track?.addEventListener('click', (e) => this.seekAudio(e));
    this.track?.addEventListener('mousedown', (e) => this.startDragging(e));

    this.volumeIcon?.addEventListener('click', () => this.toggleMute());

    // Updated Menu Click Listener
    this.dotIcon?.addEventListener('click', (e) => {
      e.stopPropagation(); // Prevent click from immediately closing the menu
      this.toggleMenu();
    });
  }

  // --- Event Handlers & Core Logic ---

  togglePlayPause() {
    if (!this.audioElement) return;
    if (this.isPlaying) {
      this.audioElement.pause();
    } else {
      this.audioElement.play().catch(error => console.error("Audio playback failed:", error));
    }
  }

  onPlay() {
    this.isPlaying = true;
    if (this.playIcon && this.pauseIcon) {
      this.playIcon.style.display = 'none';
      this.pauseIcon.style.display = 'block';
    }
  }

  onPause() {
    this.isPlaying = false;
    if (this.playIcon && this.pauseIcon) {
      this.playIcon.style.display = 'block';
      this.pauseIcon.style.display = 'none';
    }
  }

  onAudioEnded() {
    if (!this.track || !this.indicator || !this.audioElement) return;
    this.onPause(); // Visually switch to play icon
    this.audioElement.currentTime = 0; // Reset for replay
    this.indicator.style.width = '0px'; // Reset progress bar
    this.updateTimeDisplay();
  }

  updateProgress() {
    if (!this.audioElement || !this.indicator || !this.track || this.isDragging || isNaN(this.audioElement.duration)) return;
    const trackWidth = this.track.getBoundingClientRect().width;
    const progress = (this.audioElement.currentTime / this.audioElement.duration) * trackWidth;
    this.indicator.style.width = `${progress}px`;
    this.updateTimeDisplay();
  }

  updateTimeDisplay() {
    if (this.timeDisplay && this.audioElement) {
      const current = this.formatTime(this.audioElement.currentTime);
      const duration = this.formatTime(this.audioElement.duration);
      this.timeDisplay.textContent = `${current} / ${duration}`;
    }
  }

  formatTime(seconds: number): string {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  seekAudio(e: MouseEvent) {
    if (!this.track || !this.audioElement || isNaN(this.audioElement.duration)) return;
    const rect = this.track.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const progressRatio = Math.max(0, Math.min(1, clickX / rect.width));
    this.audioElement.currentTime = progressRatio * this.audioElement.duration;
    this.updateProgress(); // Immediately update visuals
  }

  startDragging(e: MouseEvent) {
    this.isDragging = true;
    document.addEventListener('mousemove', this.onDraggingHandler);
    document.addEventListener('mouseup', this.stopDraggingHandler);
    this.seekAudio(e);
  }

  onDragging(e: MouseEvent) {
    if (this.isDragging) {
      e.preventDefault();
      this.seekAudio(e);
    }
  }

  stopDragging() {
    this.isDragging = false;
    document.removeEventListener('mousemove', this.onDraggingHandler);
    document.removeEventListener('mouseup', this.stopDraggingHandler);
  }

  toggleMute() {
    if (!this.audioElement) return;
    this.audioElement.muted = !this.audioElement.muted;
  }

  updateVolumeIcon() {
    if (!this.audioElement) return;

    const volumeStates = [
      this.volumeMuteIcon,
      this.volumeLowIcon,
      this.volumeMediumIcon,
      this.volumeHighIcon
    ];

    volumeStates.forEach(icon => {
      if (icon) icon.style.display = 'none';
    });

    const volume = this.audioElement.muted ? 0 : this.audioElement.volume;
    let activeIcon: HTMLElement | null = null;

    if (volume === 0) {
      activeIcon = this.volumeMuteIcon;
    } else if (volume <= 0.25) {
      activeIcon = this.volumeLowIcon;
    } else if (volume <= 0.5) {
      activeIcon = this.volumeMediumIcon;
    } else {
      activeIcon = this.volumeHighIcon;
    }

    if (activeIcon) {
      activeIcon.style.display = 'block';
    }
  }

  // --- Menu Methods ---

  private toggleMenu() {
    if (this.isMenuVisible) {
      this.hideMenu();
    } else {
      this.showMenu();
    }
  }

  private showMenu() {
    if (!this.menu || !this.audioElement) return;

    // Set the download link's href and download attributes
    const downloadLink = this.menu.querySelector('.download-link') as HTMLAnchorElement;
    if (downloadLink) {
      downloadLink.href = this.audioElement.src;
      // Attempt to create a sensible filename from the URL
      try {
        const url = new URL(this.audioElement.src);
        const pathParts = url.pathname.split('/');
        downloadLink.download = pathParts[pathParts.length - 1] || 'audio.mp3';
      } catch (e) {
        // Fallback for invalid URLs or other errors
        downloadLink.download = 'audio.mp3';
      }
    }

    this.menu.style.display = 'block';
    this.isMenuVisible = true;
    // Add a listener to close the menu when clicking anywhere else
    // Use setTimeout to prevent the current click from being captured
    setTimeout(() => document.addEventListener('click', this.onOutsideClickHandler), 0);
  }

  private hideMenu() {
    if (!this.menu) return;
    this.menu.style.display = 'none';
    this.isMenuVisible = false;
    // Clean up the listener to avoid it firing unnecessarily
    document.removeEventListener('click', this.onOutsideClickHandler);
  }

  private handleOutsideClick() {
    // This will be called on any click outside the component when the menu is open
    this.hideMenu();
  }
}

// Define the custom element for the browser to use
customElements.define('audio-player', AudioPlayer);